import { call, put } from 'redux-saga/effects';
import { Formatters, Messages } from '../../../../codegen/Localize';
import { callViaApiService, GroupDatabasesRequestBody, GroupDatabasesResponse } from '../../../../ts/restApi/GroupDatabases';
import { openConfirmActionDialog } from '../../../../ts/services/ReactConfirmActionDialog';
import { RestApiErrorResponse } from '../../../../ts/services/RestApiService';
import { ToasterService, ToastType } from '../../../../ts/services/ToasterService';
import { DatabaseForGrouping, GroupDatabasesAction } from '../../../actions/spaces/GroupDatabasesAction';
import { requestSpaceContents } from '../../../actions/spaces/RequestSpaceContentsActions';
import { delayPromise } from '../../../utils/Delay';
import { ConnectedGroupDatabasesDialogBody, GroupDatabasesDialogBodyProps, GroupDatabasesDialogState } from '../../../content/catalog/GroupDatabasesDialog';
import { reloadSpaceIfUpdatingCurrentSpace } from './RefreshContentSagas';
import { HelpService } from '../../../../commonInfra/services/HelpService';
import { ValidTelemetryEvent, Telemetry } from '../../../../commonInfra/services/Telemetry';

enum GroupDatabasesTelemetryEvents {
  clickedMenuItem = 'GroupDatabasesMenu_ClickedItem'
}

interface GroupDatabasesMenuItemClickedEvent extends ValidTelemetryEvent<GroupDatabasesMenuItemClickedEvent> {
  type: GroupDatabasesTelemetryEvents.clickedMenuItem;
  embeddedNonEmbeddedMix: boolean;
}

function sendGroupDatabaseMenuItemClickedTelemetry(embeddedNonEmbeddedMix: boolean): void {
  Telemetry.sendEvent<GroupDatabasesMenuItemClickedEvent>({
    type: GroupDatabasesTelemetryEvents.clickedMenuItem,
    embeddedNonEmbeddedMix
  });
}

export function* handleGroupDatabases(action: GroupDatabasesAction) {
  const embedMix: boolean = isMixOfEmbeddedAndNonEmbedded(action.items);
  // Log a telemetry event indicating whether we could display the group dialog.
  yield call(sendGroupDatabaseMenuItemClickedTelemetry, embedMix);

  if (embedMix) {
    ToasterService.instance.pop({
      type: ToastType.Error,
      text: Messages.groupDatabases_mixEmbedStatusError(),
    });
    return;
  }

  try {
    yield call(openDialog, action.items);
  } catch {
    return;
  }

  const maybeReloaded = yield call(reloadSpaceIfUpdatingCurrentSpace, action.items);
  if (!maybeReloaded) {
    yield put(requestSpaceContents());
  }
}

// Exported so we can test this.
// Returns true if there is more than one distinct embeddedness (yes, no) among the list of databases for grouping.
export function isMixOfEmbeddedAndNonEmbedded(items: ReadonlyArray<DatabaseForGrouping>): boolean {
  return items.some(x => x.isEmbedded) && items.some(x => !x.isEmbedded);
}

function openDialog(databases: ReadonlyArray<DatabaseForGrouping>) {
  const modalInstance = openConfirmActionDialog<GroupDatabasesDialogBodyProps, GroupDatabasesDialogState, GroupDatabasesResponse>({
    title: Messages.groupDatabases_dialogTitle(),
    bodyComponent: ConnectedGroupDatabasesDialogBody,
    initialBodyProps: { databases },
    initialDialogState: { primaryDatabaseLuid: databases[0].luid },
    actionName: Messages.groupDatabases_submit({ COUNT: databases.length, COUNT_LOC: Formatters.formatCount(databases.length) }),
    action: makeApiCallAndHandleResult,
  });

  return modalInstance.result;
}


export async function makeApiCallAndHandleResult(props: GroupDatabasesDialogBodyProps, dialogState: GroupDatabasesDialogState): Promise<GroupDatabasesResponse> {
  const body: GroupDatabasesRequestBody = {
    databaseGroup: {
      metadataDatabaseId: dialogState.primaryDatabaseLuid,
      database: props.databases.map(item => ({ id: item.luid })),
    },
  };

  const toaster = ToasterService.instance;
  const learnMoreToastUrl = HelpService.getServerHelpUrlByCategory('group-ungroup-timing');

  try {
    const result = await callViaApiService(body);
    await delayPromise(2000); // Wait 2 seconds to allow the result to propagate in the backend
    toaster.pop({
      type: ToastType.Info,
      text: Messages.groupDatabases_toastSuccess(),
      actionMessage: Messages.groupDatabases_learnMore(),
      action: () => { window.open(learnMoreToastUrl, '_blank'); }
    });
    return result;

  } catch (e) {
    const errorResponse = e as RestApiErrorResponse;
    toaster.pop({
      type: ToastType.Error,
      text: errorResponse.data.error.detail,
    });
    throw errorResponse;
  }
}
